import { useTranslation } from 'react-i18next';
import {
  TARGET_DEVICE_NOTE_ID,
  TARGET_FIELD_ID,
  TARGET_STEP_KRONER
} from '../../constants/overviewConstants';
import { useKronerInput } from '../../hooks/useKronerInput';
import { getDkkSymbol } from '../../utils/currencyUtils';
import styles from './TargetField.module.css';

interface TargetFieldProps {
  target: number | null;
  onChange: (target: number | null) => void;
}

/**
 * The year's money ceiling, typed in on the year plan.
 *
 * A plain input rather than the library number field for the same reason the
 * completion amount uses one: that field coerces a cleared value to 0 on blur,
 * and 0 kr is a very different statement from "no target set".
 *
 * Whole kroner only. A government allocation is not quoted in øre, and the
 * figures here run to eight digits where two decimals would only add noise.
 */
export const TargetField = ({ target, onChange }: TargetFieldProps) => {
  const { t, i18n } = useTranslation();
  // Whole kroner, and zero is not an amount: a ceiling of zero is not a thing a
  // location can be given, and clearing the field is how "no target" is
  // expressed. Matches how the group reimbursement fields treat it since
  // REH-319.
  const { text, handleChange, handleBlur, handleKeyDown } = useKronerInput(
    target ?? undefined,
    (value) => onChange(value ?? null),
    { maxDecimals: 0, allowZero: false }
  );

  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={TARGET_FIELD_ID}>
        {t('year-plan-target-label')}
      </label>
      {/* INTERIM. The target is held per browser until the backend field lands
          (REH-340), so the figure genuinely does not travel to a colleague or to
          this planner's other machine — and the field invites relying on it.
          Said out loud rather than left for someone to discover. A description
          rather than part of the label, so the field is still just "Year
          target". Delete with the localStorage seam in REH-341. */}
      <span id={TARGET_DEVICE_NOTE_ID} className={styles.deviceNote}>
        {t('year-plan-target-device-note')}
      </span>
      <div className={styles.inputWrapper}>
        <input
          id={TARGET_FIELD_ID}
          type="number"
          className={styles.input}
          aria-describedby={TARGET_DEVICE_NOTE_ID}
          value={text}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          min={TARGET_STEP_KRONER}
          step={TARGET_STEP_KRONER}
          placeholder={t('year-plan-target-placeholder')}
        />
        <span className={styles.suffix} aria-hidden="true">
          {getDkkSymbol(i18n.language)}
        </span>
      </div>
    </div>
  );
};
